import { useMemo } from 'react';
import type { Appointment } from '@/types';
import type { PatientWithDoctors } from '@/services/api/patients.service';

interface CalendarDayViewProps {
  date: Date;
  appointments: Appointment[];
  patients: PatientWithDoctors[];
  onAppointmentClick: (appointment: Appointment) => void;
  onTimeSlotClick: (startTime: Date) => void;
}

export function CalendarDayView({
  date,
  appointments,
  patients,
  onAppointmentClick,
  onTimeSlotClick,
}: CalendarDayViewProps) {
  // Generate time slots from 7 AM to 8 PM (13 hours = 26 slots of 30 min)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 19; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const slotDate = new Date(date);
        slotDate.setHours(hour, minute, 0, 0);
        slots.push(slotDate);
      }
    }
    return slots;
  }, [date]);

  const getPatientName = (patientId?: string | null) => {
    if (!patientId) return 'Sem paciente';
    const patient = patients.find((p) => p.id === patientId);
    return patient?.fullName || 'Paciente não encontrado';
  };

  const getAppointmentsForSlot = (slotDate: Date) => {
    return appointments.filter((apt) => {
      const aptStart = new Date(apt.startUtc);
      const aptEnd = new Date(apt.endUtc);
      const slotEnd = new Date(slotDate.getTime() + 30 * 60 * 1000); // 30 min later

      return (
        (aptStart >= slotDate && aptStart < slotEnd) ||
        (aptStart <= slotDate && aptEnd > slotDate)
      );
    });
  };

  const calculateAppointmentHeight = (appointment: Appointment) => {
    const start = new Date(appointment.startUtc);
    const end = new Date(appointment.endUtc);
    const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    // Each slot is 60px (h-16), representing 30 minutes
    return Math.max(60, (durationMinutes / 30) * 60);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Realizada':
        return 'bg-green-100 border-green-500 hover:bg-green-200 text-green-900';
      case 'Cancelada':
        return 'bg-red-100 border-red-500 hover:bg-red-200 text-red-900';
      case 'Agendada':
      default:
        return 'bg-blue-100 border-blue-500 hover:bg-blue-200 text-blue-900';
    }
  };

  const getStatusBadge = (status?: string) => {
    if (!status || status === 'Agendada') return null;
    
    const badgeColors = {
      'Realizada': 'bg-green-500 text-white',
      'Cancelada': 'bg-red-500 text-white',
    };

    return (
      <span className={`text-xs px-2 py-0.5 rounded ${badgeColors[status as keyof typeof badgeColors]}`}>
        {status}
      </span>
    );
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b">
        <h3 className="font-semibold text-lg">
          {date.toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </h3>
      </div>

      {/* Time Grid */}
      <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
        <div className="relative">
          {timeSlots.map((slot, index) => {
            const slotAppointments = getAppointmentsForSlot(slot);
            const isHourStart = slot.getMinutes() === 0;

            return (
              <div
                key={index}
                className={`relative border-b ${
                  isHourStart ? 'border-gray-300' : 'border-gray-100'
                } h-16 ${slotAppointments.length === 0 ? 'hover:bg-gray-50' : ''} cursor-pointer transition-colors group`}
                onClick={() => onTimeSlotClick(slot)}
              >
                {/* Time Label */}
                {isHourStart && (
                  <div className="absolute left-0 top-0 w-16 text-xs text-gray-500 font-medium px-2 py-1">
                    {formatTime(slot)}
                  </div>
                )}

                {/* Appointments */}
                <div className="absolute left-16 right-0 top-0 bottom-0 px-2">
                  {slotAppointments.map((appointment) => {
                    const start = new Date(appointment.startUtc);
                    const isStartSlot =
                      start.getHours() === slot.getHours() &&
                      start.getMinutes() === slot.getMinutes();

                    // For non-start slots, render an invisible clickable overlay
                    if (!isStartSlot) {
                      return (
                        <div
                          key={appointment.id}
                          className="absolute left-2 right-2 top-0 bottom-0 cursor-pointer z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAppointmentClick(appointment);
                          }}
                        />
                      );
                    }

                    const height = calculateAppointmentHeight(appointment);

                    return (
                      <div
                        key={appointment.id}
                        className={`absolute left-2 right-2 top-1 border-l-4 rounded p-2 transition-colors cursor-pointer shadow-sm overflow-hidden ${getStatusColor(appointment.status)}`}
                        style={{ height: `${height - 8}px` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(appointment);
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-semibold truncate flex-1">
                            {appointment.title}
                          </div>
                          {getStatusBadge(appointment.status)}
                        </div>
                        <div className="text-xs opacity-80 truncate">
                          {getPatientName(appointment.patientId)}
                        </div>
                        <div className="text-xs opacity-70 mt-1">
                          {formatTime(new Date(appointment.startUtc))} -{' '}
                          {formatTime(new Date(appointment.endUtc))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* New Appointment Hint - only show if no appointments in this slot */}
                {slotAppointments.length === 0 && (
                  <div className="absolute left-16 right-0 top-0 bottom-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <span className="text-xs text-gray-400">+ Novo agendamento</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
